import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/lib/admin';
import { AdminShell } from '@/components/app/AdminShell';

const PLAN_TONES: Record<string, string> = {
  free: 'bg-ink-100 text-ink-700 border-ink-200',
  growth: 'bg-accent-50 text-accent-700 border-accent-200',
  global: 'bg-citation-50 text-citation-700 border-citation-200',
  enterprise: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

export function AdminCompaniesPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'companies', search],
    queryFn: () => adminApi.listCompanies({ search: search || undefined, limit: 100 }),
  });

  return (
    <AdminShell
      title={t('admin.companies.title', { defaultValue: 'Companies' })}
      description={t('admin.companies.subtitle', { defaultValue: 'Every tenant on the platform. Plan, seat allocation, deployment, region.' }) as string}
      actions={
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('admin.companies.search_placeholder', { defaultValue: 'Search by name…' }) as string}
          className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
      }
    >
      {isLoading || !data ? (
        <div className="card-paper p-10 text-center text-ink-500 text-sm">{t('common.loading')}</div>
      ) : data.rows.length === 0 ? (
        <div className="card-paper p-12 text-center text-ink-500 text-sm">
          {t('admin.companies.empty', { defaultValue: 'No companies match this filter.' })}
        </div>
      ) : (
        <div className="card-paper overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-ink-500 border-b border-ink-100 bg-paper-100 text-[11px] uppercase tracking-[0.12em]">
                <th className="py-2.5 pl-5 pr-3 font-medium">{t('admin.companies.th_company')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.th_plan')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.th_seats')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.th_deploy')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.companies.th_created')}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((c) => (
                <tr key={c.id} className="border-b border-ink-50 align-top hover:bg-paper-100/40">
                  <td className="py-3 pl-5 pr-3">
                    <div className="text-ink-900 font-medium">{c.name}</div>
                    <div className="text-[11px] text-ink-500 num mt-0.5">{c.id.slice(0, 8)} · {c.countryCode}</div>
                  </td>
                  <td className="py-3 px-3">
                    <span className={`rounded-md text-[10.5px] px-1.5 py-0.5 border font-semibold uppercase tracking-[0.1em] ${PLAN_TONES[c.plan] ?? PLAN_TONES.free}`}>
                      {c.plan}
                    </span>
                  </td>
                  <td className="py-3 px-3 num text-ink-700">
                    {c.memberCount} / {c.seatLimit}
                  </td>
                  <td className="py-3 px-3 text-[12px] text-ink-700 capitalize">{c.deploymentKind}</td>
                  <td className="py-3 px-3 num text-[12px] text-ink-600 whitespace-nowrap">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-3 text-[11px] text-ink-500 border-t border-ink-100">
            {t('admin.companies.showing', {
              defaultValue: 'Showing {{shown}} of {{total}}',
              shown: data.rows.length,
              total: data.total,
            })}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
